import {
  AlignmentType,
  BorderStyle,
  Document,
  Footer,
  Header,
  HeadingLevel,
  Packer,
  PageNumber,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from "docx";
import ExcelJS from "exceljs";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export type ExportIndicatorRow = {
  readonly libelle: string;
  readonly serviceLibelle: string;
  readonly frequence: string;
  readonly valeur: string;
  readonly unite: string;
};

export type ExportIndicatorsMeta = {
  readonly title: string;
  readonly subtitle?: string;
  readonly generatedAtLabel: string;
  readonly filterSummary: string;
  readonly columns: {
    readonly indicateur: string;
    readonly service: string;
    readonly frequence: string;
    readonly valeur: string;
    readonly unite: string;
  };
  readonly footerNote: string;
  readonly brand: string;
};

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function tableBody(rows: readonly ExportIndicatorRow[]) {
  return rows.map((r) => [r.libelle, r.serviceLibelle, r.frequence, r.valeur, r.unite]);
}

export async function exportIndicatorsPdf(
  rows: readonly ExportIndicatorRow[],
  meta: ExportIndicatorsMeta,
  filename = "indicateurs-suivi-se.pdf",
) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 14;

  const drawChrome = (pageNumber: number, pageCount: number) => {
    doc.setFillColor(27, 67, 50);
    doc.rect(0, 0, pageWidth, 22, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(meta.brand, marginX, 9);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const titleLines = doc.splitTextToSize(meta.title, pageWidth - marginX * 2);
    doc.text(titleLines.slice(0, 2), marginX, 15.5);

    doc.setFillColor(245, 247, 245);
    doc.rect(0, pageHeight - 16, pageWidth, 16, "F");
    doc.setDrawColor(27, 67, 50);
    doc.setLineWidth(0.4);
    doc.line(marginX, pageHeight - 16, pageWidth - marginX, pageHeight - 16);
    doc.setTextColor(60, 70, 65);
    doc.setFontSize(8);
    doc.text(meta.footerNote, marginX, pageHeight - 10);
    doc.text(meta.generatedAtLabel, marginX, pageHeight - 5);
    doc.text(`${pageNumber} / ${pageCount}`, pageWidth - marginX, pageHeight - 7.5, {
      align: "right",
    });
  };

  autoTable(doc, {
    startY: 30,
    head: [
      [
        meta.columns.indicateur,
        meta.columns.service,
        meta.columns.frequence,
        meta.columns.valeur,
        meta.columns.unite,
      ],
    ],
    body: tableBody(rows),
    styles: {
      font: "helvetica",
      fontSize: 8,
      cellPadding: 2.2,
      textColor: [30, 40, 35],
      lineColor: [210, 220, 212],
      lineWidth: 0.2,
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fillColor: [45, 90, 39],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 8.5,
    },
    alternateRowStyles: {
      fillColor: [248, 250, 248],
    },
    columnStyles: {
      0: { cellWidth: 90 },
      1: { cellWidth: 55 },
      2: { cellWidth: 30 },
      3: { cellWidth: 28, halign: "center" },
      4: { cellWidth: 30 },
    },
    margin: { top: 28, left: marginX, right: marginX, bottom: 22 },
  });

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    drawChrome(i, pageCount);
    if (i === 1 && meta.filterSummary) {
      doc.setTextColor(80, 90, 85);
      doc.setFontSize(8);
      doc.text(meta.filterSummary, marginX, 26);
    }
  }

  doc.save(filename);
}

function border() {
  return {
    top: { style: BorderStyle.SINGLE, size: 4, color: "C8D2CB" },
    bottom: { style: BorderStyle.SINGLE, size: 4, color: "C8D2CB" },
    left: { style: BorderStyle.SINGLE, size: 4, color: "C8D2CB" },
    right: { style: BorderStyle.SINGLE, size: 4, color: "C8D2CB" },
  };
}

function cell(text: string, opts?: { bold?: boolean; fill?: string; width?: number }) {
  return new TableCell({
    width: { size: opts?.width ?? 1800, type: WidthType.DXA },
    borders: border(),
    ...(opts?.fill ? { shading: { fill: opts.fill } } : {}),
    children: [
      new Paragraph({
        children: [
          new TextRun({
            text,
            ...(opts?.bold ? { bold: true } : {}),
            size: 18,
            font: "Calibri",
            color: opts?.fill === "2D5A27" ? "FFFFFF" : "1E2823",
          }),
        ],
      }),
    ],
  });
}

export async function exportIndicatorsWord(
  rows: readonly ExportIndicatorRow[],
  meta: ExportIndicatorsMeta,
  filename = "indicateurs-suivi-se.docx",
) {
  const headerRow = new TableRow({
    children: [
      cell(meta.columns.indicateur, { bold: true, fill: "2D5A27", width: 4200 }),
      cell(meta.columns.service, { bold: true, fill: "2D5A27", width: 2600 }),
      cell(meta.columns.frequence, { bold: true, fill: "2D5A27", width: 1600 }),
      cell(meta.columns.valeur, { bold: true, fill: "2D5A27", width: 1200 }),
      cell(meta.columns.unite, { bold: true, fill: "2D5A27", width: 1400 }),
    ],
  });

  const bodyRows = rows.map((r, index) => {
    const fill = index % 2 === 1 ? "F8FAF8" : undefined;
    const opts = fill ? { width: 4200, fill } : { width: 4200 };
    return new TableRow({
      children: [
        cell(r.libelle, opts),
        cell(r.serviceLibelle, fill ? { width: 2600, fill } : { width: 2600 }),
        cell(r.frequence, fill ? { width: 1600, fill } : { width: 1600 }),
        cell(r.valeur, fill ? { width: 1200, fill } : { width: 1200 }),
        cell(r.unite, fill ? { width: 1400, fill } : { width: 1400 }),
      ],
    });
  });

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: { top: 1000, bottom: 1000, left: 720, right: 720 },
          },
        },
        headers: {
          default: new Header({
            children: [
              new Paragraph({
                shading: { fill: "1B4332" },
                spacing: { after: 80 },
                children: [
                  new TextRun({
                    text: `  ${meta.brand}  `,
                    bold: true,
                    color: "FFFFFF",
                    size: 20,
                    font: "Calibri",
                  }),
                ],
              }),
              new Paragraph({
                spacing: { after: 60 },
                children: [
                  new TextRun({
                    text: meta.title,
                    bold: true,
                    size: 22,
                    color: "1B4332",
                    font: "Calibri",
                  }),
                ],
              }),
              ...(meta.filterSummary
                ? [
                    new Paragraph({
                      spacing: { after: 120 },
                      children: [
                        new TextRun({
                          text: meta.filterSummary,
                          italics: true,
                          size: 16,
                          color: "5A655E",
                          font: "Calibri",
                        }),
                      ],
                    }),
                  ]
                : []),
            ],
          }),
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                border: {
                  top: { style: BorderStyle.SINGLE, size: 12, color: "1B4332", space: 8 },
                },
                alignment: AlignmentType.LEFT,
                children: [
                  new TextRun({
                    text: `${meta.footerNote}  ·  ${meta.generatedAtLabel}  ·  `,
                    size: 14,
                    color: "5A655E",
                    font: "Calibri",
                  }),
                  new TextRun({
                    children: [PageNumber.CURRENT],
                    size: 14,
                    color: "5A655E",
                    font: "Calibri",
                  }),
                  new TextRun({
                    text: " / ",
                    size: 14,
                    color: "5A655E",
                    font: "Calibri",
                  }),
                  new TextRun({
                    children: [PageNumber.TOTAL_PAGES],
                    size: 14,
                    color: "5A655E",
                    font: "Calibri",
                  }),
                ],
              }),
            ],
          }),
        },
        children: [
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            spacing: { after: 200 },
            children: [
              new TextRun({
                text: meta.subtitle ?? meta.columns.indicateur,
                color: "1B4332",
                font: "Calibri",
              }),
            ],
          }),
          new Table({
            width: { size: 11000, type: WidthType.DXA },
            rows: [headerRow, ...bodyRows],
          }),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  downloadBlob(blob, filename);
}

export async function exportIndicatorsExcel(
  rows: readonly ExportIndicatorRow[],
  meta: ExportIndicatorsMeta,
  filename = "indicateurs-suivi-se.xlsx",
) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = meta.brand;
  workbook.created = new Date();

  const sheet = workbook.addWorksheet("Matrice indicateurs", {
    views: [{ state: "frozen", ySplit: 6 }],
    pageSetup: {
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
    },
    headerFooter: {
      oddHeader: `&L&B${meta.brand}&R&D`,
      oddFooter: `&L${meta.footerNote}&C&P / &N&R${meta.generatedAtLabel}`,
    },
  });

  sheet.columns = [
    { key: "libelle", width: 48 },
    { key: "service", width: 32 },
    { key: "frequence", width: 16 },
    { key: "valeur", width: 12 },
    { key: "unite", width: 14 },
  ];

  const brandRow = sheet.addRow([meta.brand]);
  brandRow.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12, name: "Calibri" };
  brandRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1B4332" },
  };
  sheet.mergeCells(1, 1, 1, 5);

  const titleRow = sheet.addRow([meta.title]);
  titleRow.font = { bold: true, color: { argb: "FF1B4332" }, size: 11, name: "Calibri" };
  titleRow.alignment = { wrapText: true, vertical: "middle" };
  sheet.mergeCells(2, 1, 2, 5);
  titleRow.height = 36;

  const filterRow = sheet.addRow([meta.filterSummary]);
  filterRow.font = { italic: true, color: { argb: "FF5A655E" }, size: 9, name: "Calibri" };
  sheet.mergeCells(3, 1, 3, 5);

  const generatedRow = sheet.addRow([meta.generatedAtLabel]);
  generatedRow.font = { color: { argb: "FF5A655E" }, size: 9, name: "Calibri" };
  sheet.mergeCells(4, 1, 4, 5);

  sheet.addRow([]);

  const header = sheet.addRow([
    meta.columns.indicateur,
    meta.columns.service,
    meta.columns.frequence,
    meta.columns.valeur,
    meta.columns.unite,
  ]);
  header.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10, name: "Calibri" };
  header.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF2D5A27" },
    };
    cell.alignment = { vertical: "middle", wrapText: true };
    cell.border = {
      top: { style: "thin", color: { argb: "FFC8D2CB" } },
      left: { style: "thin", color: { argb: "FFC8D2CB" } },
      bottom: { style: "thin", color: { argb: "FFC8D2CB" } },
      right: { style: "thin", color: { argb: "FFC8D2CB" } },
    };
  });

  for (const [index, row] of rows.entries()) {
    const excelRow = sheet.addRow([
      row.libelle,
      row.serviceLibelle,
      row.frequence,
      row.valeur,
      row.unite,
    ]);
    const alt = index % 2 === 1;
    excelRow.eachCell((cell, colNumber) => {
      cell.font = { size: 9, name: "Calibri", color: { argb: "FF1E2823" } };
      cell.alignment = {
        vertical: "middle",
        wrapText: true,
        horizontal: colNumber === 4 ? "center" : "left",
      };
      if (alt) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF8FAF8" },
        };
      }
      cell.border = {
        top: { style: "thin", color: { argb: "FFC8D2CB" } },
        left: { style: "thin", color: { argb: "FFC8D2CB" } },
        bottom: { style: "thin", color: { argb: "FFC8D2CB" } },
        right: { style: "thin", color: { argb: "FFC8D2CB" } },
      };
    });
  }

  const footerRow = sheet.addRow([]);
  footerRow.getCell(1).value = meta.footerNote;
  footerRow.font = { color: { argb: "FF5A655E" }, size: 8, italic: true, name: "Calibri" };
  sheet.mergeCells(footerRow.number, 1, footerRow.number, 5);

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    filename,
  );
}
